import { aggregate, QueryFields, readItem, readItems } from "@directus/sdk";
import axios from "axios";
import { GaripalliDirectusClient } from "../directus/client";
import { Match } from "../directus/collections";
import { Schema } from "../directus/schema";
import { isTransientHttpError, withRetry } from "../libs/retry";
import { splitIntoChunks } from "../libs/utils";
import { asDirectusMatch, asLandbotMatch } from "../utils/matches";

export const FULL_DIRECTUS_MATCH_FIELDSET: QueryFields<Schema, Match> = [
  "*",
  "ratings.*",
  "owner.id",
  "owner.email",
  "current_scene.translations.*",
];

Object.freeze(FULL_DIRECTUS_MATCH_FIELDSET);

export async function fetchDirectusMatchById(
  client: GaripalliDirectusClient,
  id: string,
  fields: QueryFields<Schema, Match> = FULL_DIRECTUS_MATCH_FIELDSET
): Promise<Match | null> {
  const response = await client.request<Match>(
    readItem("matches", id, { fields })
  );
  return asDirectusMatch(response);
}

export async function fetchDirectusMatches(
  client: GaripalliDirectusClient,
  fields: QueryFields<Schema, Match> = FULL_DIRECTUS_MATCH_FIELDSET,
  props?: { limit: number; offset: number }
) {
  const response = await client.request<Match[]>(
    readItems("matches", {
      fields: fields,
      limit: props?.limit,
      offset: props?.offset,
      sort: ["-date_created"],
    })
  );

  return asDirectusMatch(response);
}

export async function fetchAllDirectusMatches(
  client: GaripalliDirectusClient,
  fields: QueryFields<Schema, Match> = FULL_DIRECTUS_MATCH_FIELDSET
) {
  // console.log("Fetching all matches with fields:", fields);
  let events: Match[] = [];
  let accumulator: Match[] = [];
  let offset = 0;
  const limit = 5000;
  do {
    events = await withRetry(
      () => fetchDirectusMatches(client, fields, { limit, offset }),
      { shouldRetry: isTransientHttpError }
    );
    accumulator.push(...events);
    offset += limit;
  } while (events.length === limit);

  return asDirectusMatch(accumulator); // Return a shallow copy of each match
}

export async function bulkFetchDirectusMatchesByIds(
  client: GaripalliDirectusClient,
  ids: string[]
): Promise<Match[]> {
  if (ids.length === 0) return [];

  // console.log(`Fetching ${ids.length} matches by IDs in bulk...`);
  const idsChunks = splitIntoChunks(ids, 50); // Too many IDs can cause URL length issues
  const parallelBatches = splitIntoChunks(idsChunks, 5); // Limit to 5 parallel calls to avoid overwhelming the server

  const results: Match[] = [];
  for (const batch of parallelBatches) {
    const batchResults = await Promise.all(
      batch.map((chunk) =>
        withRetry(
          () =>
            client.request<Match[]>(
              readItems("matches", { filter: { id: { _in: chunk } } })
            ),
          { shouldRetry: isTransientHttpError }
        )
      )
    );
    results.push(...batchResults.flat());
  }

  return asDirectusMatch(results);
}

export async function fetchAllDirectusMatchesCreatedAfterGivenDate(
  client: GaripalliDirectusClient,
  date: string,
  fields: QueryFields<Schema, Match> = FULL_DIRECTUS_MATCH_FIELDSET
) {
  // console.log("Fetching matches after date:", date);

  let events: Match[] = [];
  let accumulator: Match[] = [];
  let offset = 0;
  const limit = 5000;
  do {
    events = await withRetry(
      () =>
        client.request<Match[]>(
          readItems("matches", {
            fields: fields,
            limit: limit,
            offset: offset,
            filter: {
              date_created: {
                _gt: date,
              },
            },
            sort: ["-date_created"],
          })
        ),
      { shouldRetry: isTransientHttpError }
    );
    accumulator.push(...events);
    offset += limit;
  } while (events.length === limit);

  return asDirectusMatch(accumulator);
}

export async function fetchDirectusChallengeMatches(
  client: GaripalliDirectusClient,
  challengeId?: string
) {
  // console.log("Fetching matches for challengeId:", challengeId);
  if (!challengeId) {
    return Promise.resolve([]); // Return an empty array if no challengeId is provided
  }

  const response = await client.request<Match[]>(
    readItems("matches", {
      filter: {
        events: {
          _some: {
            target_id: { _eq: challengeId },
          },
        },
      },
      fields: [
        "id",
        "steps_challenge_stats",
        "date_created",
        "order_id",
        "order_type",
        "shopify_order",
        "purchased_at",
        "completed_at",
        "fulfilled",
        "ratings.*",
        "owner.id",
        "owner.email",
        "team_name",
        "account",
      ],
      limit: 10000,
    })
  );
  return asDirectusMatch(response);
}

export function fetchDirectusMatchCount(client: GaripalliDirectusClient) {
  return client.request<{ count: number }>(
    aggregate("matches", {
      aggregate: {
        count: "*",
      },
    })
  );
}

export async function fetchLandbotMatches(
  page: number,
  perPage: number
): Promise<Match[]> {
  const url = new URL(`/analytics/matches`, import.meta.env.VITE_API_V2_URL);
  url.searchParams.append("page", page.toString());
  url.searchParams.append("per_page", perPage.toString());

  const response = await axios.get<{ matches: Match[] }>(url.toString());
  if (response.status !== 200) {
    throw new Error(`Failed to fetch Landbot matches: ${response.statusText}`);
  }
  const matches = await response.data.matches;
  return asLandbotMatch(matches);
}

export async function fetchAllLandbotMatches(): Promise<Match[]> {
  let allMatches: Match[] = [];
  let page = 1;
  const perPage = 1000;
  let fetchedMatches: Match[] = [];
  do {
    fetchedMatches = await withRetry(
      () => fetchLandbotMatches(page, perPage),
      { shouldRetry: isTransientHttpError }
    );
    allMatches = allMatches.concat(fetchedMatches);
    page++;
  } while (fetchedMatches.length === perPage);

  return asLandbotMatch(allMatches);
}
